<template>
  <v-row justify="center">
    <x-dialog :show="state.show" maxwidth="380" persistent content-class="v-dialog-c">
      <x-card color="white" class="pt-6">
        <x-card-text>
          <!-- TOTP enabled: code entry -->
          <div v-if="isTotp && hasTotp">
            <div class="text-center font-weight-bold mb-1" style="color: black !important;">
              Verify it's you
            </div>
            <div class="basic--text text-center mb-3">
              Enter the 6-digit code from your authenticator app to continue.
            </div>

            <x-text-field
              dense outlined rounded
              placeholder="Code..."
              maxlength="6"
              v-model="code"
              @keyup.enter="verify"
            ></x-text-field>

            <div v-if="showError" class="mb-3 error--text">
              Invalid code. Please try again.
            </div>

            <x-button
              width="100%" color="primary" depressed elevation="0" rounded
              :loading="loading"
              :disabled="loading || code == null || code.length !== 6"
              @click.stop="verify"
            >Verify</x-button>
          </div>

          <!-- TOTP required but not enabled: role-aware enable-first -->
          <div v-else-if="isTotp && !hasTotp">
            <v-alert type="warning" class="my-1">
              <span v-if="isAdmin">
                Two-factor authentication is required for this action. Enable it to continue.
              </span>
              <span v-else>
                Two-factor authentication is required for this action and must be set up
                by ICT for your account.
              </span>
            </v-alert>
            <x-button
              v-if="isAdmin"
              width="100%" color="primary" depressed elevation="0" rounded
              @click="goToSettings"
            >Go to settings</x-button>
          </div>

          <!-- passkey verification -->
          <div v-else-if="isPasskey">
            <div class="text-center font-weight-bold mb-1" style="color: black !important;">
              Verify it's you
            </div>
            <div class="basic--text text-center mb-3">
              Use your passkey (fingerprint, face, or device PIN) to continue.
            </div>

            <div v-if="passkeyError" class="mb-3 error--text">
              {{ passkeyError }}
            </div>

            <x-button
              width="100%" color="primary" depressed elevation="0" rounded
              :loading="loading"
              :disabled="loading"
              @click.stop="verifyWithPasskey"
            >Verify with passkey</x-button>
          </div>

          <!-- unsupported method -->
          <div v-else>
            <v-alert type="info" class="my-1">
              This verification method is not available.
            </v-alert>
          </div>

          <x-button
            width="100%" class="my-2 error" dense elevation="0" rounded
            style="color: white"
            :disabled="loading"
            @click="cancel"
          >Cancel</x-button>
        </x-card-text>
      </x-card>
    </x-dialog>
  </v-row>
</template>

<script>
import stepUpPrompt from "@/shared/stepUpPrompt";
import stepUpService from "@/shared/stepUpService";
import ErrorResolver from "@/shared/ErrorResolver";

import XButton from "@ui/widgets/XButton.vue";
import XCard from "@ui/components/XCard.vue";
import XCardText from "@ui/components/XCardText.vue";
import XTextField from "@ui/widgets/XTextField.vue";
import XDialog from "@ui/components/XDialog.vue";
import Roles from "@/shared/Roles";

export default {
  name: "StepUpDialog",
  components: { XButton, XCard, XCardText, XTextField, XDialog },

  data: () => ({
    code: "",
    loading: false,
    showError: false,
    passkeyError: "",
  }),

  computed: {
    state() {
      return stepUpPrompt.state;
    },
    isTotp() {
      return this.state.method === "totp";
    },
    isPasskey() {
      return this.state.method === "passkey";
    },
    userObj() {
      const u = this.$store && this.$store.getters.getUser;
      return (u && (u.user ? u.user : u)) || {};
    },
    hasTotp() {
      return !!this.userObj.mfa;
    },
    isAdmin() {
      const roles = this.userObj.roles || [];
      return Roles.contains(roles, Roles.Admin) || Roles.contains(roles, Roles.SuperAdmin);
    },
  },

  watch: {
    // Reset local state each time the dialog opens.
    "state.show"(open) {
      if (open) {
        this.code = "";
        this.loading = false;
        this.showError = false;
        this.passkeyError = "";
      }
    },
  },

  methods: {
    verify() {
      if (this.code == null || this.code.length !== 6) return;
      this.loading = true;
      this.showError = false;

      // Resolve the userId from this portal's store (admin = flat, others = nested).
      const user = this.$store && this.$store.getters.getUser;
      const userId = (user && (user.userId || (user.user && user.user.userId))) || null;

      stepUpService
        .verifyTotp(this.code, userId)
        .then((ok) => {
          if (ok) {
            stepUpPrompt.settle(true);
          } else {
            this.showError = true;
            this.code = "";
          }
        })
        .catch((err) => {
          this.showError = true;
          this.code = "";
          ErrorResolver.checkErrors(this, err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    cancel() {
      stepUpPrompt.settle(false);
    },

    goToSettings() {
      if (this.$router) this.$router.push({ path: "/settings" }).catch(() => {});
      stepUpPrompt.settle(false);
    },

    verifyWithPasskey() {
      this.loading = true;
      this.passkeyError = "";
      stepUpService
        .verifyPasskey()
        .then((ok) => {
          if (ok) stepUpPrompt.settle(true);
          else this.passkeyError = "Passkey verification failed. Please try again.";
        })
        .catch((e) => {
          this.passkeyError = this.mapPasskeyError(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    mapPasskeyError(e) {
      if (e && e.name === "NotAllowedError") return "Passkey prompt was dismissed. Please try again.";
      if (e && e.response && e.response.status === 401) return "That passkey was not recognised.";
      if (e && e.name === "SecurityError") return "Passkeys are unavailable on this connection.";
      return "Could not complete passkey verification.";
    },
  },
};
</script>

<style scoped></style>
